import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../../firebase/client";
import { signInWithGoogle, signOut } from "../../auth/firebaseAuth";
import "./Ops.css";

// Internal ops dashboard for pilot monitoring. Reads a super-admin-only Cloud
// Function (opsUsage) that returns metadata only — no message text / PHI. Any
// non-super-admin who reaches this page just gets "not authorized" from the
// function.
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : "—");
const daysAgo = (ms) => {
  if (!ms) return "—";
  const d = Math.floor((Date.now() - ms) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`;
};
const statusLabel = (c) => {
  if (c.plan === "comp") return "comped";
  if (c.status === "trialing") {
    const left = c.trialEndsAt ? Math.max(0, Math.ceil((c.trialEndsAt - Date.now()) / 86400000)) : null;
    return left != null ? `trial · ${left}d left` : "trial";
  }
  return c.status || "—";
};

export default function Ops() {
  const auth = useSelector((s) => s.auth);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auth || !auth.uid) return;
    setLoading(true); setError(null);
    httpsCallable(getFunctions(app, "us-central1"), "opsUsage")()
      .then((r) => setData(r.data))
      .catch((e) => setError(`${(e && e.code) || "error"}: ${(e && e.message) || "Could not load usage."}`))
      .finally(() => setLoading(false));
  }, [auth && auth.uid]);

  if (auth === null) return <div className="ops"><p className="ops-muted">Loading…</p></div>;

  if (!auth || !auth.uid) {
    return (
      <div className="ops ops-center">
        <h1>Ops · Usage</h1>
        <p className="ops-muted">Sign in with an authorized account to view pilot usage.</p>
        <button className="ops-btn" onClick={() => signInWithGoogle()}>Continue with Google</button>
      </div>
    );
  }

  return (
    <div className="ops">
      <div className="ops-head">
        <h1>Ops · Usage <span className="ops-muted">— pilot &amp; Care Circle activity</span></h1>
        <div className="ops-muted">
          {data ? `Updated ${new Date(data.generatedAt).toLocaleString()}` : ""}{" "}
          · {auth.email} · <button className="ops-link" onClick={() => signOut()}>Sign out</button>
        </div>
      </div>

      {loading && <p className="ops-muted">Loading usage…</p>}
      {error && <p className="ops-error">{error}</p>}

      {data && (
        <>
          <div className="ops-tiles">
            <div className="ops-tile"><div className="k">Circles</div><div className="v">{data.totals.circles}</div><div className="n">{data.totals.trialing} on trial</div></div>
            <div className="ops-tile"><div className="k">Active (7d)</div><div className="v">{data.totals.active7}</div><div className="n">{data.totals.active30} in 30d</div></div>
            <div className="ops-tile"><div className="k">Screens claimed</div><div className="v">{data.totals.screensClaimed}</div><div className="n">{data.totals.residents} residents</div></div>
            <div className="ops-tile"><div className="k">Care Circle members</div><div className="v">{data.totals.members}</div><div className="n">across all circles</div></div>
            <div className="ops-tile"><div className="k">Messages</div><div className="v">{data.totals.messages}</div><div className="n">{data.totals.photos} photos</div></div>
          </div>

          <div className="ops-card">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Care circle</th><th>Status</th><th className="num">Age</th>
                  <th className="num">Members</th><th className="num">Residents</th><th className="num">Screens</th>
                  <th className="num">Msgs</th><th className="num">Remind</th><th className="num">Photos</th>
                  <th className="num">Notif</th><th>Last active</th><th>Started</th>
                </tr>
              </thead>
              <tbody>
                {data.circles.map((c) => {
                  const stale = !c.lastActivity || Date.now() - c.lastActivity > 14 * 86400000;
                  const notStarted = c.messages === 0 && c.photos === 0;
                  return (
                    <tr key={c.tenantId} className={notStarted ? "ops-row-warn" : ""}>
                      <td>{c.name}</td>
                      <td>{statusLabel(c)}</td>
                      <td className="num">{c.ageDays != null ? `${c.ageDays}d` : "—"}</td>
                      <td className="num">{c.members}</td>
                      <td className="num">{c.residents}</td>
                      <td className="num">{c.devicesClaimed}/{c.devices}</td>
                      <td className="num">{c.messages}</td>
                      <td className="num">{c.reminders}</td>
                      <td className="num">{c.photos}</td>
                      <td className="num">{c.notifTokens}</td>
                      <td className={stale ? "ops-stale" : "ops-ok"}>{daysAgo(c.lastActivity)}</td>
                      <td>{fmtDate(c.createdAt)}</td>
                    </tr>
                  );
                })}
                {data.circles.length === 0 && <tr><td colSpan="12" className="ops-muted">No care circles yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="ops-muted ops-foot">
            Metadata only — no message content, resident names, or photos. Highlighted rows have posted nothing yet
            (not activated). "Last active" is derived from message/photo timestamps recorded going forward.
          </p>
        </>
      )}
    </div>
  );
}
